import createIntlWithLocale from 'src/internal/createIntlWithLocale';
import {handleReactIntlError} from 'src/internal/errors/handleReactIntlError/handleReactIntlError';
import mergeOptions from 'src/internal/mergeOptions';
import fixNegativeZero from 'src/internal/number-formatter/fixNegativeZero';
import fixRounding from 'src/internal/number-formatter/fixRounding';
import resolveFinalNotation from 'src/internal/resolveFinalNotation';
import formatNumberCompactDigitDefaults from 'src/public/formatNumber/formatNumberCompactDigitDefaults';

import type {IAnalytics, IReports} from '@sail/observability';
import type {
  FormatNumberOptions,
  FormatNumberOptionsDefaults,
  FormatNumberOptionsWithDefaults,
} from 'src/public/formatNumber/types';
import type {IntlConfig} from 'src/types';

/**
 * @see https://trailhead.corp.stripe.com/docs/sail-intl/api/useIntl#format-number
 */
export default function formatNumber(
  /** The intl config object containing the locale data. When using this API through the `useIntl` hook, this config object is automatically injected. */
  config: IntlConfig,
  reports: IReports,
  analytics: IAnalytics,
  /** The number to be formatted. */
  number: number,
  /** An object with options to customize the formatting. */
  options?: FormatNumberOptions,
): string {
  const resolvedNotation = resolveFinalNotation(
    options?.forceCompactAboveValue,
    number,
    options?.notation,
  );

  const defaults: FormatNumberOptionsDefaults =
    resolvedNotation === 'compact'
      ? {
          locale: config.locale,
          ...formatNumberCompactDigitDefaults,
        }
      : {
          locale: config.locale,
          maximumFractionDigits: 3,
        };

  const optionsWithDefaults: FormatNumberOptionsWithDefaults = mergeOptions(
    config,
    defaults,
    reports,
    options,
    {
      style: 'decimal',
      notation: resolvedNotation,
    },
  );

  let consolidatedNumber: number;
  consolidatedNumber = fixNegativeZero(number);
  consolidatedNumber = fixRounding(
    consolidatedNumber,
    optionsWithDefaults.maximumFractionDigits,
    optionsWithDefaults.roundingMode,
  );

  const intl = createIntlWithLocale(
    optionsWithDefaults.locale,
    config,
    reports,
    analytics,
  );

  try {
    return intl.formatNumber(consolidatedNumber, optionsWithDefaults);
  } catch (error) {
    handleReactIntlError(reports, analytics, config.locale, error);

    return '';
  }
}

export type FormatNumber = typeof formatNumber;

export type FormatNumberDeclarative = (
  number: Parameters<FormatNumber>[3],
  options?: Parameters<FormatNumber>[4],
) => ReturnType<FormatNumber>;
